import { Loader2, Mic, Square } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import type { MirrorPresenceState } from "@/components/mirror/MirrorPresenceConfig";
import type { MirrorLiveEndReason, MirrorLiveStatus } from "@/hooks/useMirrorLive";

// ─────────────────────────────────────────────────────────────────────────────
// MirrorLiveControls — o pé da tela /mirror/live no Realtime (LOTE 3).
//
// Três coisas, nesta ordem visual: o MEDIDOR de tempo restante (feature
// cobrada por minuto sem mostrar o saldo faz o usuário bater a parede no
// meio de uma frase), o rótulo de estado (pedindo microfone · conectando ·
// ouvindo · falando · fim), e UM botão: começar quando parado, encerrar
// quando ao vivo. O botão é o gesto do usuário — é dentro dele que
// getUserMedia, o AudioContext e o autoplay do áudio remoto ficam
// permitidos no iOS/Safari.
//
// Dumb por desenho: estado e efeitos vivem no useMirrorLive.
// ─────────────────────────────────────────────────────────────────────────────

export interface MirrorLiveControlCopy {
  start: string; end: string; ready: string; requestingMic: string; connecting: string;
  listening: string; thinking: string; speaking: string; remaining: string; remainingAria: string;
  ended: Record<string, string>;
}

interface MirrorLiveControlsProps {
  /** A module can use its selected native language without changing the app locale. */
  copy?: MirrorLiveControlCopy;
  disabled?: boolean;
  status: MirrorLiveStatus;
  presence: MirrorPresenceState;
  endReason: MirrorLiveEndReason | null;
  /** Segundos de conversa ao vivo restantes no mês (null = ainda não sabemos). */
  secondsRemaining: number | null;
  onStart: () => void;
  onEnd: () => void;
}

/** 0 → "0:00", 275 → "4:35", 3600 → "60:00". */
export function formatRemaining(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return String(m) + ":" + (r < 10 ? "0" : "") + String(r);
}

/** Chave i18n do fim da sessão por motivo — uma por motivo, todas nos 6 locales. */
export function endReasonKey(reason: MirrorLiveEndReason): string {
  switch (reason) {
    case "quota":
    case "no_quota":
      return "mirror.live.ended.quota";
    case "expired":
      return "mirror.live.ended.expired";
    case "mic_denied":
      return "mirror.live.ended.micDenied";
    case "disconnected":
      return "mirror.live.ended.disconnected";
    case "error":
      return "mirror.live.ended.error";
    case "disabled":
      return "mirror.live.ended.disabled";
    case "client":
    default:
      return "mirror.live.ended.client";
  }
}

export default function MirrorLiveControls({
  status,
  presence,
  endReason,
  secondsRemaining,
  onStart,
  onEnd,
  copy,
  disabled = false,
}: MirrorLiveControlsProps) {
  const { t, tf } = useLanguage();

  const busy = status === "requesting_mic" || status === "connecting";
  const isLive = status === "live";

  let label: string;
  if (status === "requesting_mic") label = copy?.requestingMic ?? t("mirror.live.state.requestingMic");
  else if (status === "connecting") label = copy?.connecting ?? t("mirror.live.state.connecting");
  else if (status === "ended" && endReason) label = copy?.ended[endReason === "no_quota" ? "quota" : endReason] ?? t(endReasonKey(endReason));
  else if (isLive) {
    label = {
      idle: (copy?.ready ?? t("mirror.ready")),
      listening: (copy?.listening ?? t("mirror.listening")),
      thinking: (copy?.thinking ?? t("mirror.reflecting")),
      speaking: (copy?.speaking ?? t("mirror.speaking")),
    }[presence];
  } else label = (copy?.ready ?? t("mirror.ready"));

  // Medidor: visível assim que o servidor disse quanto sobra (token/tique) e
  // enquanto houver sessão ou o fim tiver sido por cota (0:00 explica o fim).
  const showMeter = secondsRemaining !== null && (isLive || busy || endReason === "quota" || endReason === "no_quota");
  const meterText = secondsRemaining === null ? "" : formatRemaining(secondsRemaining);
  const meterLow = secondsRemaining !== null && secondsRemaining <= 60;

  return (
    <div className="flex flex-col items-center gap-3 mt-2" data-testid="mirror-live-controls">
      {showMeter && (
        <div
          role="timer"
          aria-live="polite"
          aria-label={copy?.remainingAria ?? t("mirror.live.meter.aria")}
          className="font-display text-[12px] tracking-[0.2em] uppercase px-3 py-1 rounded-full"
          style={{
            color: meterLow ? "hsl(0 60% 70%)" : "hsl(var(--primary) / 0.85)",
            background: "hsl(222 35% 9% / 0.72)",
            border: "1px solid " + (meterLow ? "hsl(0 60% 55% / 0.4)" : "hsl(var(--primary) / 0.25)"),
            backdropFilter: "blur(12px)",
          }}
        >
          {copy ? copy.remaining.replace("{time}", meterText) : tf("mirror.live.remaining", { time: meterText })}
        </div>
      )}

      <p
        className="text-center text-[11px] font-display tracking-[0.25em] uppercase text-foreground/60 px-6"
        aria-live="polite"
      >
        {label}
      </p>

      <button
        type="button"
        disabled={busy || disabled}
        onClick={isLive ? onEnd : onStart}
        aria-label={isLive ? (copy?.end ?? t("mirror.live.end")) : (copy?.start ?? t("mirror.live.start"))}
        className="relative flex items-center justify-center"
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: isLive
            ? "radial-gradient(circle, hsl(0 60% 45%), hsl(0 60% 25%))"
            : "radial-gradient(circle, hsl(187 72% 45%), hsl(187 72% 25%))",
          border: isLive ? "2px solid hsl(0 60% 65%)" : "2px solid hsl(187 72% 65%)",
          boxShadow: isLive
            ? "0 0 0 8px hsl(0 60% 55% / 0.12), 0 0 24px hsl(0 60% 55% / 0.35)"
            : "0 0 0 8px hsl(187 72% 55% / 0.15), 0 0 24px hsl(187 72% 55% / 0.4)",
          transition: "all 0.3s ease",
          opacity: busy ? 0.6 : 1,
          cursor: busy ? "wait" : "pointer",
        }}
      >
        {busy ? (
          <Loader2 size={26} color="white" className="animate-spin" />
        ) : isLive ? (
          <Square size={22} color="white" fill="currentColor" />
        ) : (
          <Mic size={28} color="white" />
        )}
      </button>

      <span className="text-[10px] font-display tracking-[0.2em] uppercase text-foreground/45">
        {isLive ? (copy?.end ?? t("mirror.live.end")) : (copy?.start ?? t("mirror.live.start"))}
      </span>
    </div>
  );
}
